// Angular
import {Injectable, Injector} from '@angular/core';

// NGRX & RXJS
import { take } from 'rxjs/operators';
import {select, Store} from '@ngrx/store';

// OLCI
import * as tracking from '../../../actions/tracking';

// Miscellaneous
import {Guest, ICreateUpdateOnboardPaymentPayload} from '../../../models';
import * as Utils from '../../../util';
import {
  IPayment,
  CreditCard,
  AccountCard,
  GuestOnboardExpenses,
  GuestsPaymentAccount
} from '../../../models/guest-oexpenses';
import { analyticsConstants, constants } from '../../../constants';
import {GuestService} from './guest.service';
import * as fromRoot from '../../../reducers';
import {BookingService} from './booking.service';
import {OExpensesUtils} from './oexpenses.utils';
import { PaymentType } from '../../../models/guest-oexpenses-payment-type';
import { PostOnboardExpenseAction } from '../../../actions';
import { IRetrieveOnboardPaymentPayload } from '../../../models/action-payloads/retrieve-onboard-payment-payload';
import { TealiumService } from './tealium.service';
import { TelemetryErrorData } from '../../../shared/interfaces';
import { ErrorService } from './error.service';
import { EndPointsErrorCode } from '../../../shared/types/endpoints-error-codes.type';

@Injectable()
export class OexpensesService {
  public oexpenses: GuestOnboardExpenses[];
	public accountCards: AccountCard[] = [];
	public guestsPaymentAccounts: GuestsPaymentAccount[] = [];

	private paymentChannelsDictionary: { [brand: string]: string } = {};
	private guests: Guest[] | null = [];

  private guestService: GuestService;
  private tealiumService: TealiumService;
  private bookingService: BookingService;
  private errorService: ErrorService;

	constructor(
    private injector : Injector,
		private readonly store: Store<fromRoot.AppState>,
		private oExpenseUtils: OExpensesUtils
	) {
    this.injectServices();

		this.paymentChannelsDictionary[constants.BRANDS_NAMES.ROYAL] = constants.PAYMENT_GATEWAY.ROYAL_CHANNEL;
		this.paymentChannelsDictionary[constants.BRANDS_NAMES.CELEBRITY] = constants.PAYMENT_GATEWAY.CELEBRITY_CHANNEL;

		store.pipe(select(fromRoot.getOexpenseState)).subscribe(oexpenses => {
			if (oexpenses && oexpenses.accountCards) {
				this.accountCards = oexpenses.accountCards;
			}
			if (oexpenses && oexpenses.guestsPaymentAccounts) {
				this.guestsPaymentAccounts = oexpenses.guestsPaymentAccounts;
			}
		});
		store.pipe(select(fromRoot.getEntitySGuests)).subscribe(guests => (this.guests = guests));
	}

  injectServices(): void {
    this.guestService = this.injector.get<GuestService>(GuestService);
    this.bookingService = this.injector.get<BookingService>(BookingService);
    this.tealiumService = this.injector.get<TealiumService>(TealiumService);
    this.errorService = this.injector.get<ErrorService>(ErrorService);
  }

	isOnboardExpensesCompleted() {
		let result = false;
		this.store.pipe(select(fromRoot.getSelectdGuestsForCheckinProcess), take(1)).subscribe(guests => {
			result = guests.every((guest: any) => this.guestsPaymentAccounts.findIndex(account => account.guestId === guest.guestId) !== -1);
		});
		return result;
	}

	getGuestOExpenses(guestId: number) {
		return this.guestsPaymentAccounts.find(expenses => expenses.guestId === guestId);
	}

	isFullCreditCard() {
		this.store.pipe(select(fromRoot.getSessionVar), take(1)).subscribe(session => {
			return session.fullCreditCard;
		});
	}

	getGranteesPerPaymentType(guestId: number, paymentType?: number) {
		if (this.guestsPaymentAccounts) {
			const guestPaymentAccount = paymentType
				? this.guestsPaymentAccounts.find(account => account.guestId === guestId && account.expensesAccount.paymentType === paymentType)
				: this.guestsPaymentAccounts.find(account => account.guestId === guestId);
			if (guestPaymentAccount) {
				return guestPaymentAccount.expensesAccount.granteeList ?? [];
			}
		}
	}

	isCCGrantor(guestId: number): boolean {
		const grantees = this.getGranteesPerPaymentType(guestId, PaymentType.CreditCard);
		if (grantees) {
			return grantees.length > 0;
		}
		return false;
	}

	isGrantee(guestId: number) {
		const grantees = this.getGranteesPerPaymentType(guestId);
		return grantees.length === 0;
	}

	getGrantorExpensesInfo(guestId: number) {
		return this.getGuestOExpenses(guestId);
	}

	formatCreditCard(creditCardHolder: string, cardType: string, partialNumber: string) {
		const ccMap = {
			VI: 'visa',
			DI: 'discover',
			MC: 'master-card',
			AX: 'american-express',
			JC: 'jcb',
			DC: 'diners-club'
		};

		const capitalizedFullName = Utils.capitalizeString(creditCardHolder);
		const fullNameCapSuffix = Utils.allCapsSuffix(capitalizedFullName);

		return {
			name: fullNameCapSuffix,
			cardType: (ccMap as any)[cardType],
			lastFour: partialNumber
		};
	}

	formatPaymentAccountCreditCard(guestId: number): any {
		const guestExpensesInfo = this.getGrantorExpensesInfo(guestId);
		if (!guestExpensesInfo) {
			return '';
		}
		const creditCard = guestExpensesInfo.expensesAccount.creditCard;
		return this.formatCreditCard(creditCard.holderName, creditCard.cardType, creditCard.partialNumber);
	}

	formatSavedCreditCard(accountCard: AccountCard): any {
		return this.formatCreditCard(accountCard.card.holderName, accountCard.card.cardTypeCode, accountCard.card.partialNumber);
	}

	addGrantee(guestId: number, creditCard: CreditCard) {
		const grantee = this.guestService.getGuest(guestId);
		const grantorPayment = { guest: grantee, paymentType: PaymentType.CreditCard, cc: creditCard };
		const grantorPaymentList: IPayment[] = [grantorPayment];
		const createUpdateOnboardPaymentRequestPayload: ICreateUpdateOnboardPaymentPayload = this.oExpenseUtils.createUpdateOnboardPaymentRequestPayload(grantorPaymentList);
		this.store.dispatch(new PostOnboardExpenseAction(createUpdateOnboardPaymentRequestPayload));
	}

	getGuestReservationId(guestsIds: number[]) {
		if (!guestsIds.length) {
			return [];
		}
    return this.guests ? this.guests
      .filter(guest => guestsIds.indexOf(guest.guestId) !== -1)
      .map(guest => ({guestReservationId: {bookingId: Number(guest.bookingId), guestId: Number(guest.guestId)}})) : null;
	}

	upsertGuestPaymentType(guestPayment: IPayment) {
		const guestPaymentList: IPayment[] = [];
		guestPaymentList.push(guestPayment);
		const createUpdateOnboardPaymentRequestPayload: ICreateUpdateOnboardPaymentPayload = this.oExpenseUtils.createUpdateOnboardPaymentRequestPayload(guestPaymentList);
		this.store.dispatch(new PostOnboardExpenseAction(createUpdateOnboardPaymentRequestPayload));
	}

	getRetrieveOnboardPaymentPayload(): IRetrieveOnboardPaymentPayload {
		return {
			grantees: this.guestService.getAllGuestsBookingCombination(this.guests).map((guest: any) => {
				return { guestReservationId: guest };
			})
		};
	}

	getPaymentGatewayChannel() {
		const brand = this.bookingService.getBrand();
		return this.paymentChannelsDictionary[brand];
	}

  sendEventToAnalyticsAndTelemetry(
    warningMessage: string,
    url: string,
    telemetryErrorData: TelemetryErrorData,
    errorCode: EndPointsErrorCode
  ) {
    const {
      ONBOARD_EXPENSES: {
        MESSAGES: { UNABLE_TO_COMPLETE_REQUEST },
      },
    } = analyticsConstants;
    const errorMessage =
      UNABLE_TO_COMPLETE_REQUEST + '|warningMessage: ' + warningMessage;

    this.errorService
      .defaultTelemetryHttpCatch(
        { message: errorMessage, developerMessage: warningMessage },
        telemetryErrorData,
        url
      )
      .subscribe({
        error: (err) => {
          this.dispatchInlineAlertEvent(
            errorMessage,
            errorCode,
            url,
            err.errorIdentifier
          );
        },
      });
  }

  private dispatchInlineAlertEvent(
    message: string,
    errorCode: EndPointsErrorCode,
    endpoint: string,
    errorIdentifier: string
  ): void {
    this.store.dispatch(
      new tracking.TrackInlineAlertEventAction(
        this.retrieveResponseToDispatch(
          message,
          errorCode,
          endpoint,
          errorIdentifier
        )
      )
    );
  }

  private retrieveResponseToDispatch(
    message: string,
    errorCode: EndPointsErrorCode,
    endpoint: string,
    errorIdentifier: string
  ) {
    const { PAGE_NAME, INTERACTION } =
      analyticsConstants.ONBOARD_EXPENSES.ADD_NEW_INVALID_CC;
    return this.tealiumService.retrieveAnalyticsAlertInlineResponse(
      PAGE_NAME,
      INTERACTION,
      message,
      false,
      {
        errorCode,
        backendError: message,
        url: endpoint,
        errorIdentifier,
      }
    );
  }
}
